import React, { useState } from "react";
import { OrderLineItem } from "./types";
import { OrderLineitemCard } from "./OrderLineitemCard";
import { Money } from "@/helpers/Money";
import { variantSelect, getSizeInfo } from "@/store";
import { parseDeliveryFrequency } from "./helper";
import { Button } from "@/components/Button";
import { useAccountpageContext } from "./AccountPageContext";
import { useCustomer } from "../CustomerContext";
import { ShopifyAddress } from "./types";
import { matchedRechargeAddress } from "./helper";
import { fetchUpcomingCharge, ModifiedCharge } from "./RechargeApi";
import { useRechargeCustomer } from "./RechargeCustomerContext";
import { shopifyCartApi } from "@/services/shopifyCart";

type OrderLineItemsProps = {
  orderderLineItems: OrderLineItem[];
  customerUrl?: string;
  orderId: string;
  subsDrawerAction: (
    addressid: number,
    upcomingCharge: ModifiedCharge | null
  ) => void;
  shippingAddress?: ShopifyAddress;
};

// Helper function to retrieve the selling plan based on plan name.
const getSellingPlan = (
  orderLineItem: OrderLineItem | undefined,
  planName = "Delivery every 4 Months"
) => {
  return orderLineItem?.product?.selling_plan_groups?.[0]?.selling_plans?.find(
    (plan) => plan.name === planName
  );
};

// Helper function to extract discount value from the selling plan.
const getDiscountValue = (orderLineItem: OrderLineItem | undefined) => {
  const sellingPlan = getSellingPlan(orderLineItem);
  return sellingPlan?.price_adjustments &&
    sellingPlan.price_adjustments.length > 0
    ? sellingPlan.price_adjustments[0].value
    : null;
};

export const OrderLineItems = ({
  orderderLineItems,
  orderId,
  subsDrawerAction,
  shippingAddress,
}: OrderLineItemsProps) => {
  const customer = useCustomer();
  const { setSelectedLineItem } = useAccountpageContext() || {};
  const { hash, token, addressess } = useRechargeCustomer() || {};
  const [loading, setLoading] = useState(false);
  const shopifyCartAdder = shopifyCartApi.useTrackedAdd();
  const [lineKey, setLineKey] = useState<null | string>(null);

  const redirectUserThroughCheckout = (
    selectedLineItem: OrderLineItem | undefined
  ) => {
    if (
      selectedLineItem === undefined ||
      selectedLineItem?.product?.selling_plan_groups === undefined ||
      selectedLineItem?.product?.selling_plan_groups?.length === 0 ||
      !selectedLineItem.variantId
    ) {
      return;
    }
    setLoading(true);
    const sellingPlan = getSellingPlan(selectedLineItem);
    if (!sellingPlan) {
      return;
    }
    if (!sellingPlan) {
      return;
    }
    const body = {
      items: [
        {
          id: selectedLineItem.variantId,
          quantity: selectedLineItem.quantity,
          selling_plan: sellingPlan.id,
        },
      ],
      sourceModule: "account-page-save-with-sub-btn",
    };
    shopifyCartAdder.mutate(body, {
      onSuccess: () => {
        setLoading(false);
        window.location.href = "/checkout";
      },
      onError: () => {
        setLoading(false);
      },
    });
  };

  const handleSubscriptionPurchase = async (key: string) => {
    setSelectedLineItem && setSelectedLineItem(orderId, key);
    setLineKey(key);

    const selectedLineItem = orderderLineItems.find(
      (lineItem) => lineItem.key === key
    );

    if (
      customer?.hasSubscriptions &&
      shippingAddress &&
      addressess &&
      addressess?.length > 0
    ) {
      if (!token || !hash) return null;
      setLoading(true);
      const rechargeAddress = matchedRechargeAddress(
        addressess,
        shippingAddress
      );

      if (rechargeAddress && rechargeAddress.id) {
        let upcomingCharge = null;
        try {
          upcomingCharge = await fetchUpcomingCharge(
            rechargeAddress.id,
            hash,
            token
          );
          setLoading(false);
        } catch (error) {
          setLoading(false);
        } finally {
          setLoading(false);
        }
        subsDrawerAction(rechargeAddress.id, upcomingCharge);
      } else {
        redirectUserThroughCheckout(selectedLineItem);
      }
    } else {
      //redirect to the checkout
      redirectUserThroughCheckout(selectedLineItem);
    }
  };

  return (
    <ul className="space-y-4">
      {orderderLineItems.map((orderderLineItem, index) => {
        // Hidden the paper insert SKU based on barcode
        if (
          orderderLineItem.product &&
          orderderLineItem.product.id === 6906068828259
        )
          return null;
        const variantObject = orderderLineItem.variant && {
          ...orderderLineItem.variant,
          size: getSizeInfo(orderderLineItem.variant.options),
        };
        const isSellingPlanAvailable =
          (orderderLineItem?.product?.selling_plan_groups?.[0]?.selling_plans
            ?.length ?? 0) > 0;

        const isVariantAvailable =
          orderderLineItem.variant &&
          orderderLineItem.variant?.available &&
          orderderLineItem.variantId;

        const productCardvalue = {
          quantity: orderderLineItem.quantity,
          product: {
            title: orderderLineItem.productTitle,
            featuredImage: {
              ...orderderLineItem.image,
            },
            size: variantObject ? variantSelect.sizeFormat(variantObject) : "",
          },
          price: Money.from(orderderLineItem.finalPrice, "¢"),
          productAvailable:
            orderderLineItem.product?.published_at &&
            isVariantAvailable &&
            isSellingPlanAvailable
              ? true
              : false,
        };

        const shipingInfo = orderderLineItem?.sellingPlanName
          ? parseDeliveryFrequency(orderderLineItem.sellingPlanName)
          : undefined;

        const discountValue = getDiscountValue(orderderLineItem);

        const buttons = (
          <>
            {orderderLineItem.product?.published_at &&
              orderderLineItem.variantId && (
                <>
                  {shipingInfo && (
                    <Button
                      variant="ghost"
                      className="h-12 w-full space-x-2 px-2 py-3 text-center only:col-span-2 md:h-full md:px-4"
                      size="sm"
                      component={"a"}
                      target="_blank"
                      href={`/products/${orderderLineItem.product.handle}#reviews`}
                      aria-label={`Leave Review for ${orderderLineItem.product.title} on order ${orderId}`}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        fill="none"
                        aria-label="Icon for leave review button"
                      >
                        <path
                          style={{ fill: "hsl(var(--color-primary))" }}
                          d="M10.667 1.333v6H7.333v3.334h-6V1.333h9.334Zm0-1.333H1.333C.6 0 0 .6 0 1.333v9.334C0 11.4.6 12 1.333 12H8l4-4V1.333C12 .6 11.4 0 10.667 0ZM6 7.333H2.667V6H6v1.333Zm3.333-2.666H2.667V3.333h6.666v1.334Z"
                        />
                      </svg>
                      <span>Leave Review</span>
                    </Button>
                  )}

                  {!shipingInfo &&
                    isSellingPlanAvailable &&
                    isVariantAvailable && (
                      <Button
                        variant="ghost"
                        className="h-12 w-full space-x-2 px-2 py-3 text-center only:col-span-2 md:h-full md:px-4"
                        size="sm"
                        component={"button"}
                        onClick={() => {
                          handleSubscriptionPurchase(orderderLineItem.key);
                        }}
                        disabled={loading}
                        loading={loading && lineKey === orderderLineItem.key}
                        data-account-btn="save-and-subscribe"
                      >
                        <span>
                          {discountValue
                            ? `Subscribe and Save ${discountValue}%`
                            : "Subscribe"}
                        </span>
                      </Button>
                    )}
                </>
              )}
            {orderderLineItem.fulfillment ? (
              <Button
                variant="ghost"
                className="h-12 w-full space-x-2 px-2 py-3 text-center only:col-span-2 md:h-full md:px-4"
                size="sm"
                component={"a"}
                href={orderderLineItem.fulfillment?.trackingUrl}
                target="_blank"
                aria-label={`Track Delivery of  ${orderderLineItem?.product?.title} for order ${orderId}`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="14"
                  fill="none"
                  aria-label="Icon for track order button"
                >
                  <path
                    style={{ fill: "hsl(var(--color-primary))" }}
                    d="M5.333 11.95V7.383l-4-2.316v4.566l4 2.317Zm1.334 0 4-2.317V5.067l-4 2.316v4.567Zm-1.334 1.533L.667 10.8A1.299 1.299 0 0 1 0 9.65v-5.3A1.299 1.299 0 0 1 .667 3.2L5.333.517a1.31 1.31 0 0 1 1.334 0L11.333 3.2A1.298 1.298 0 0 1 12 4.35v5.3a1.298 1.298 0 0 1-.667 1.15l-4.666 2.683a1.311 1.311 0 0 1-1.334 0Zm3.334-8.8L9.95 3.95 6 1.667l-1.3.75 3.967 2.266ZM6 6.233l1.3-.75L3.35 3.2l-1.3.75L6 6.233Z"
                  />
                </svg>
                <span>Track</span>
              </Button>
            ) : (
              <Button
                variant="ghost"
                className="h-12 w-full space-x-2 px-2 py-3 text-center only:col-span-2 md:h-full md:px-4"
                size="sm"
                component="button"
                aria-label={`Tracking Delivery of  ${orderderLineItem?.product?.title} for order ${orderId} is not available yet`}
                disabled={true}
                aria-disabled="true"
                title={`Tracking Delivery of  ${orderderLineItem?.product?.title} for order ${orderId} is not available yet`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="14"
                  fill="none"
                  aria-label="Icon for track order button"
                >
                  <path
                    style={{ fill: "hsl(var(--color-primary))" }}
                    d="M5.333 11.95V7.383l-4-2.316v4.566l4 2.317Zm1.334 0 4-2.317V5.067l-4 2.316v4.567Zm-1.334 1.533L.667 10.8A1.299 1.299 0 0 1 0 9.65v-5.3A1.299 1.299 0 0 1 .667 3.2L5.333.517a1.31 1.31 0 0 1 1.334 0L11.333 3.2A1.298 1.298 0 0 1 12 4.35v5.3a1.298 1.298 0 0 1-.667 1.15l-4.666 2.683a1.311 1.311 0 0 1-1.334 0Zm3.334-8.8L9.95 3.95 6 1.667l-1.3.75 3.967 2.266ZM6 6.233l1.3-.75L3.35 3.2l-1.3.75L6 6.233Z"
                  />
                </svg>
                <span>Track</span>
              </Button>
            )}
          </>
        );

        return (
          <li key={orderderLineItem.productTitle + index}>
            <OrderLineitemCard
              {...productCardvalue}
              responsive={true}
              scentBg="white"
              shippingInfo={shipingInfo}
              orderItemCardActions={buttons}
              discountValue={discountValue}
              onSubClick={() => {
                handleSubscriptionPurchase(orderderLineItem.key);
              }}
            />
          </li>
        );
      })}
    </ul>
  );
};
